import React, { useCallback, useEffect, useRef, useState } from 'react';
import { SeoManager, getPageSeo, buildFAQSchema } from '../seo';
import FAQSection, { reactionTestFaqs } from '../components/FAQSection';
import {
  DELAY_MAX_MS,
  DELAY_MIN_MS,
  LIGHT_COUNT,
  LIGHT_INTERVAL_MS,
  getJumpStartRank,
  getReactionRank,
  randomLightsOutDelay,
} from '../utils/reactionTest';
import { getItem, setItem } from '../utils/safeStorage';
import '../styles/ReactionTest.css';

const BEST_KEY = 'rt-reaction-best-ms';

const ACTIVE_PHASES = new Set(['lighting', 'armed', 'go']);

export default function ReactionTestPage() {
  const [phase, setPhase] = useState('idle');
  const [lightsOn, setLightsOn] = useState(0);
  const [reactionMs, setReactionMs] = useState(null);
  const [jumpStart, setJumpStart] = useState(false);
  const [bestMs, setBestMs] = useState(null);
  const [rank, setRank] = useState(null);
  const phaseRef = useRef('idle');
  const lightsOutAtRef = useRef(null);
  const timersRef = useRef([]);

  const setPhaseSafe = useCallback((next) => {
    phaseRef.current = next;
    setPhase(next);
  }, []);

  const clearTimers = useCallback(() => {
    timersRef.current.forEach((id) => clearTimeout(id));
    timersRef.current = [];
  }, []);

  useEffect(() => {
    const stored = Number(getItem(BEST_KEY));
    if (Number.isFinite(stored) && stored > 0) setBestMs(stored);
  }, []);

  useEffect(() => () => {
    clearTimers();
  }, [clearTimers]);

  const saveBest = useCallback((ms) => {
    setBestMs((best) => {
      if (best != null && ms >= best) return best;
      setItem(BEST_KEY, String(ms));
      return ms;
    });
  }, []);

  const startRound = useCallback(() => {
    clearTimers();
    setLightsOn(0);
    setReactionMs(null);
    setJumpStart(false);
    setRank(null);
    lightsOutAtRef.current = null;
    setPhaseSafe('lighting');

    for (let i = 1; i <= LIGHT_COUNT; i += 1) {
      const lightId = setTimeout(() => {
        setLightsOn(i);
        if (i === LIGHT_COUNT) {
          setPhaseSafe('armed');
          const delay = randomLightsOutDelay();
          const goId = setTimeout(() => {
            setLightsOn(0);
            lightsOutAtRef.current = performance.now();
            setPhaseSafe('go');
          }, delay);
          timersRef.current.push(goId);
        }
      }, i * LIGHT_INTERVAL_MS);
      timersRef.current.push(lightId);
    }
  }, [clearTimers, setPhaseSafe]);

  const handleReact = useCallback(() => {
    const current = phaseRef.current;
    if (current === 'lighting' || current === 'armed') {
      clearTimers();
      setLightsOn(0);
      setJumpStart(true);
      setReactionMs(null);
      setRank(getJumpStartRank());
      setPhaseSafe('result');
      return;
    }

    if (current === 'go' && lightsOutAtRef.current != null) {
      const ms = Math.max(0, Math.round(performance.now() - lightsOutAtRef.current));
      setJumpStart(false);
      setReactionMs(ms);
      setRank(getReactionRank(ms));
      saveBest(ms);
      setPhaseSafe('result');
    }
  }, [clearTimers, saveBest, setPhaseSafe]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.code !== 'Space' && event.code !== 'Enter') return;
      if (event.repeat) return;
      event.preventDefault();
      if (phaseRef.current === 'idle' || phaseRef.current === 'result') {
        startRound();
        return;
      }
      handleReact();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [handleReact, startRound]);

  const seo = getPageSeo('reaction-test');
  const structuredData = seo?.structuredData
    ? [seo.structuredData, buildFAQSchema(reactionTestFaqs)]
    : buildFAQSchema(reactionTestFaqs);

  const clickable = ACTIVE_PHASES.has(phase);
  const statusText = jumpStart && phase === 'result'
    ? 'Jump start'
    : phase === 'go'
      ? 'Lights out — go!'
      : phase === 'armed'
        ? 'Hold… wait for lights out'
        : phase === 'lighting'
          ? 'Lights coming on — don’t jump'
          : '';

  return (
    <>
      <SeoManager
        title={seo.title}
        description={seo.description}
        keywords={seo.keywords}
        path={seo.path}
        structuredData={structuredData}
      />

      <section className="rt-game" aria-label="F1 reaction test">
        <h1 className="rt-visually-hidden">F1 Reaction Test: Click When the Lights Go Out</h1>

        <header className="rt-header">
          <div className="rt-score-block">
            <span className="rt-score-number">{bestMs != null ? `${bestMs} ms` : '—'}</span>
            <span className="rt-score-label">Best Time</span>
          </div>
          {reactionMs != null && (
            <div className="rt-score-block">
              <span className="rt-score-number">{reactionMs} ms</span>
              <span className="rt-score-label">Last Time</span>
            </div>
          )}
        </header>

        <div
          className="rt-stage"
          data-rt-phase={phase}
          data-rt-lights={lightsOn}
          data-rt-clickable={clickable ? 'true' : 'false'}
          onPointerDown={clickable ? (event) => {
            if (event.button != null && event.button !== 0) return;
            handleReact();
          } : undefined}
          role={clickable ? 'button' : undefined}
          tabIndex={clickable ? 0 : undefined}
          aria-label={clickable ? 'Click when the lights go out' : undefined}
        >
          {phase === 'idle' && (
            <div className="rt-idle-card">
              <h2>F1 Reaction Test</h2>
              <p>
                Five red lights come on one by one, like an F1 start gantry.
                After a short random delay they go out. Click, tap, or press
                Space the instant the lights go out — not before.
              </p>
              <button type="button" className="rt-btn rt-btn-primary" onClick={startRound}>
                Start Lights
              </button>
            </div>
          )}

          {phase !== 'idle' && (
            <div className="rt-playfield">
              <p
                className={`rt-status${phase === 'go' ? ' rt-status-go' : ''}${jumpStart ? ' rt-status-jump' : ''}`}
                aria-live="polite"
              >
                {statusText || '\u00a0'}
              </p>
              <div className="rt-gantry" aria-hidden="true">
                {Array.from({ length: LIGHT_COUNT }, (_, index) => (
                  <div
                    key={index}
                    className={`rt-column${index < lightsOn ? ' rt-column-on' : ''}`}
                  >
                    {Array.from({ length: 4 }, (__, bulb) => (
                      <span key={bulb} className="rt-bulb" />
                    ))}
                  </div>
                ))}
              </div>
              <div className="rt-gantry-pole" aria-hidden="true" />
            </div>
          )}

          {phase === 'result' && rank && (
            <div className={`rt-result-card${jumpStart ? ' rt-result-card-jump' : ''}`}>
              <h2>{jumpStart ? 'Jump Start' : 'Reaction Time'}</h2>
              <p className="rt-result-score">
                {jumpStart ? 'False start' : `${reactionMs} ms`}
              </p>
              <div className="rt-rank-row">
                <span className="rt-rank-label">Your Rank:</span>
                <span className="rt-rank-animal">{rank.animal}</span>
              </div>
              <p className="rt-result-label">{rank.message}</p>
              {!jumpStart && (
                <p className="rt-result-meta">
                  {bestMs === reactionMs
                    ? 'New best time in this browser.'
                    : `Best so far: ${bestMs} ms.`}
                  {' '}F1 drivers typically launch in 150–250 ms.
                </p>
              )}
              <button type="button" className="rt-btn rt-btn-primary" onClick={startRound}>
                Play Again
              </button>
            </div>
          )}
        </div>
      </section>

      <section className="rt-explainer" aria-labelledby="rt-explainer-title">
        <div className="rt-explainer-copy">
          <p className="rt-explainer-kicker">Race-start explainer</p>
          <h2 id="rt-explainer-title">
            F1 reaction time is not only speed — it is speed without guessing early.
          </h2>
          <p className="rt-explainer-lead">
            This guide connects lights-out timing, anticipation, false starts,
            and practice into one clearer F1 start context. Real drivers usually
            leave the grid in about 150–250 milliseconds.
          </p>
        </div>
        <div className="rt-explainer-panel">
          <div className="rt-explainer-stat">
            <span className="rt-explainer-value">150–250ms</span>
            <span className="rt-explainer-label">Driver range</span>
          </div>
          <div className="rt-explainer-stat">
            <span className="rt-explainer-value">&lt;250ms</span>
            <span className="rt-explainer-label">Solid start</span>
          </div>
          <div className="rt-explainer-stat">
            <span className="rt-explainer-value">Free</span>
            <span className="rt-explainer-label">Cost</span>
          </div>
          <button
            type="button"
            className="rt-btn rt-btn-practice"
            onClick={() => {
              document.querySelector('.rt-game')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
              if (phase === 'idle' || phase === 'result') startRound();
            }}
          >
            Practice lights out
          </button>
        </div>
      </section>

      <section className="rt-extras">
        <h2>How to Play the F1 Reaction Test</h2>
        <p>
          Watch the start lights. They illuminate one column per second. After
          all five are on, they go out after a random delay between{' '}
          {DELAY_MIN_MS / 1000} and {DELAY_MAX_MS / 1000} seconds. React the
          moment they go out. Clicking while any lights are still on is a jump
          start. F1 drivers typically react in 150–250 ms; under 250 ms is a
          solid start on this test.
        </p>
        <ul>
          <li>Click, tap, or press Space / Enter as soon as the red lights go out.</li>
          <li>Jump starts fail the attempt and do not save a time.</li>
          <li>Your best valid reaction time is stored in this browser.</li>
        </ul>
      </section>

      <FAQSection mode="reactionTest" />
    </>
  );
}
